import { createContext, createElement, Suspense } from "react";
import { usePreloadedQuery } from "react-relay";
import { graphql } from "relay-runtime";

import { useLoadQuery } from "@skydio/relay-toolkit";
import { Skeleton } from "@skydio/rivet-ui";

import type { PropsWithChildren } from "react";
import type { PreloadedQuery } from "react-relay";
import type {
  FeatureFlagProviderQuery,
  FeatureFlagProviderQuery$data,
} from "__generated__/FeatureFlagProviderQuery.graphql";

const query = graphql`
  query FeatureFlagProviderQuery($orgUuid: String!) {
    organization(uuid: $orgUuid) {
      id
      featureFlags {
        # REQUIRED: Global "dfr_command" feature flag - DO NOT REMOVE
        dfrCommand
        # OPTIONAL: Additional feature flags used in the application
        # These can be modified or extended as needed for specific functionality
        enableMultidroneOperations
        enableRfdDedrone
        enableDfrCommandPhaseTwoUi
        enableDfrCommandPhaseTwoPointFiveUi
        enableDfrCommandPhaseThreeUi
        externalApiIncidentMarkersUi
        filterMarkersInRfdSidebar
        enableDfrDockviewLayout
        customMapLayersExternal
        cloudRel3UiUpdates
        enableStandoffDistance
        enableLookAtFinalPoint
        enableCreateMarkerInteractionRfd
        enableDocklessSites
        multizoneSiteGeofences
        safeLandingPoints
        enableDispatchRespondHere
        # NOTE(bella) Markers CRUD API - only used to gate a mutation change before Cloud Release 3
        markersApi
        enableManualIncidentCreation
        enableIncidentsStateUi
        enableAxonWatchMeDemo
        sortDroneSelectionModalDispatch
        cloudRel4UiUpdates
        enablePostFlightReports
        cloudRel5UiUpdates
        enableDfrOutcomesSurvey
        enablePlanningAroundGeofencesFlyToPointRfd
        usePathfinderFlyToPointRfd
        apiObservability
        filterMarkerButtonInSidebar
        enableRfdViewLookatPoint
      }
    }
  }
`;

export type FeatureFlags = NonNullable<
  NonNullable<FeatureFlagProviderQuery$data["organization"]>["featureFlags"]
>;

export const featureFlagContext = createContext<FeatureFlags | null>(null);

// The useFeatureFlag hook is now provided in src/hooks/useFeatureFlag.ts
// No need to import it here to avoid circular dependencies

interface InternalProps extends PropsWithChildren {
  queryReference: PreloadedQuery<FeatureFlagProviderQuery>;
}

const FeatureFlagProviderInternal = ({ queryReference, children }: InternalProps) => {
  const queryData = usePreloadedQuery(query, queryReference);

  return (
    <featureFlagContext.Provider value={queryData.organization?.featureFlags ?? null}>
      {children}
    </featureFlagContext.Provider>
  );
};

const FeatureFlagProviderSkeleton = () => {
  // Add your own skeleton loading state here
  return <Skeleton className="h-12" />;
};

const FeatureFlagProvider = ({ orgUuid, ...props }: PropsWithChildren<{ orgUuid: string }>) => {
  const [queryReference] = useLoadQuery<FeatureFlagProviderQuery>(query, {
    orgUuid,
  });

  if (!queryReference) {
    return <FeatureFlagProviderSkeleton />;
  }

  return (
    <Suspense fallback={createElement(FeatureFlagProviderSkeleton)}>
      <FeatureFlagProviderInternal queryReference={queryReference} {...props} />
    </Suspense>
  );
};

export default FeatureFlagProvider;
